import DialogActions from "@mui/material/DialogActions";
import { Form } from "formik";
import { useDispatch, useSelector } from "react-redux";
import * as Yup from "yup";
import { getFormDiagnosis, getFormEmployers } from "../../../../actions/forms";
import { convertDateTimeToNet } from "../../../../utils/datetimeUtils";
import FormikActions from "../../../commons/formik/formikActions";
import FormikAutocomplete from "../../../commons/formik/formikAutocomplete";
import FormikDateTimePicker from "../../../commons/formik/formikDateTimePicker";
import FormikForm from "../../../commons/formik/formikForm";
import FormikTextField from "../../../commons/formik/formikTextField";
import { StyledEditAccidentForm } from "../../../styled/accident";
import { validateRequiredFields } from "../../forms/formValidation";

const validation = Yup.object({
  nroSiniestroArt: Yup.string().max(
    15,
    "El campo no puede superar los 15 caracteres",
  ),
});

const EditAccidentForm = ({
  accident,
  model,
  artId,
  handleClose,
  handleSubmit,
  fromFacade,
  onEmployerChange,
}) => {
  const dispatch = useDispatch();
  const roles = useSelector((state) => state.auth.roles);
  const diagnosis = useSelector((state) => state.forms.diagnosis);
  const empleadores = useSelector((state) => state.forms.employers);
  const trabajadores = useSelector((state) => state.forms.employeeOptions);

  const esPerfilDeSoporte = roles?.tipo === "Soporte" || roles?.tipo === "Administrador";

  const diagnosisIsFound = diagnosis.data.some(
    (d) => d.id === accident.diagnosticoOMS,
  );

  if (!diagnosisIsFound) {
    accident.diagnosticoOMS = "";
  }

  return (
    <FormikForm
      artFields={model.fields}
      initialValues={accident}
      validationSchema={validation}
      validate={(values) => validateRequiredFields(values, model)}
      onSubmit={(values) => {
        handleSubmit({
          siniestro: values.siniestro,
          nroSiniestroArt: values.nroSiniestroArt,
          idReferenteExterno: values.idReferenteExterno,
          referenteApellidoyNombre: values.referenteApellidoyNombre,
          referenteEmail: values.referenteEmail,
          fechaHoraAccidente: values.fechaHoraAccidente
            ? convertDateTimeToNet(values.fechaHoraAccidente)
            : null,
          diagnosticoOMS: values.diagnosticoOMS,
          cuilCuitTrabajador: values.cuilCuitTrabajador,
          cuitEmpleador: values.cuitEmpleador,
        });
      }}>
      {({ setFieldValue }) => (
        <Form>

          <StyledEditAccidentForm.Container>
            <FormikTextField
              variant="standard"
              name="nroSiniestroArt"
              label={`Nro Siniestro ART ${model.nroSiniestroArt.required ? "*" : ""}`}
              type="text"
              sx={StyledEditAccidentForm.input}
              disabled={!esPerfilDeSoporte && fromFacade}
            />
            <FormikAutocomplete
              variant="standard"
              name="referenteApellidoyNombre"
              label={`Referente ART ${model.referenteApellidoyNombre.required ? "*" : ""}`}
              options={model.referenteApellidoyNombre.options}
              callBack={(value) => {
                if (value) {
                  setFieldValue("idReferenteExterno", value.id);
                  setFieldValue("referenteEmail", value.email);
                } else {
                  setFieldValue("idReferenteExterno", null);
                  setFieldValue("referenteEmail", null);
                }
              }}
              returnProp="nombre"
              sx={StyledEditAccidentForm.input}
              disabled={!esPerfilDeSoporte
                && fromFacade
                && accident.referenteApellidoyNombre ? true : false
              }
            />

            <FormikDateTimePicker
              name="fechaHoraAccidente"
              label={`Fecha y Hora del Accidente ${model.fechaHoraAccidente.required ? "*" : ""}`}
              variant="standard"
              sx={StyledEditAccidentForm.input}
              disabled={!esPerfilDeSoporte && fromFacade}
            />

            <FormikAutocomplete
              name="diagnosticoOMS"
              label={`Diagnóstico OMS ${model.diagnosticoOMS.required ? "*" : ""}`}
              variant="standard"
              options={diagnosis.data}
              getOptions={() => dispatch(getFormDiagnosis(artId))}
              sx={StyledEditAccidentForm.input}
              renderOption={(option) => `${option.id} - ${option.nombre}`}
              filterOptions={(option) => option.id + option.nombre}
              disabled={!esPerfilDeSoporte && fromFacade && diagnosisIsFound}
            />

            <FormikAutocomplete
              variant="standard"
              name="empleador"
              label={`Empleador ${model.cuitEmpleador.required ? "*" : ""}`}
              sx={StyledEditAccidentForm.input}
              disabled={!esPerfilDeSoporte && fromFacade}
              options={empleadores.data}
              getOptions={() => dispatch(getFormEmployers(artId))}
              returnProp="cuit"
              labelOption={option => `${option.cuit} ${option.razonSocial.trim()}`}
              renderOption={(option) => `${option.cuit}, ${option.razonSocial.trim()}`}
              isOptionEqualToValue={(option, value) => option.cuit === value.cuit}
              filterOptions={(option) => option.cuit + ' ' + option.razonSocial}
              callBack={(value) => {
                if (value) {
                  setFieldValue("cuitEmpleador", value.cuit);
                  onEmployerChange(value);
                } else {
                  setFieldValue("cuitEmpleador", "");
                }
                setFieldValue("trabajador", "");
                setFieldValue("cuilCuitTrabajador", "");
              }}
            />
            <FormikAutocomplete
              variant="standard"
              name="trabajador"
              label={`Trabajador ${model.cuilCuitTrabajador.required ? "*" : ""}`}
              options={trabajadores.data}
              sx={StyledEditAccidentForm.input}
              disabled={!esPerfilDeSoporte && fromFacade}
              returnProp="cuilCuitTrabajador"
              labelOption={option => `${option.numeroDocumentoTrabajador} ${option.apellidoTrabajador?.trim()} ${option.nombreTrabajador?.trim()}`}
              renderOption={(option) => `${option.numeroDocumentoTrabajador} ${option.apellidoTrabajador?.trim()}, ${option.nombreTrabajador?.trim()}`}
              isOptionEqualToValue={(option, value) => {
                const optionCuil = option.cuilCuitTrabajador || option.cuilCuit;
                const valueCuil = typeof value === 'string' ? value : (value?.cuilCuitTrabajador || value?.cuilCuit);
                return optionCuil === valueCuil ||
                  option.numeroDocumentoTrabajador === (typeof value === 'object' ? value?.numeroDocumentoTrabajador : null);
              }}
              filterOptions={(option) => option.numeroDocumentoTrabajador + ' ' + option.apellidoTrabajador + ' ' + option.nombreTrabajador}
              callBack={(value) => {
                if (value) {
                  const cuil = value.cuilCuitTrabajador || value.cuilCuit;
                  setFieldValue("cuilCuitTrabajador", cuil);
                } else {
                  setFieldValue("cuilCuitTrabajador", "");
                }
              }}
            />
            <DialogActions
              sx={{
                padding: 0,
                display: "flex",
                justifyContent: "flex-end",
              }}>
              <FormikActions handleCancel={handleClose} />
            </DialogActions>
          </StyledEditAccidentForm.Container>
        </Form>
      )}
    </FormikForm>
  );
};

export default EditAccidentForm;
